import * as React from 'react';
import Title from 'antd/lib/typography/Title';
import { runwayColors, antGrey } from '../styles/globalStyles';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import { PlusOutlined, InfoCircleOutlined, ExperimentOutlined } from '@ant-design/icons';
import { InfoTip } from '../setup/shared';
import Tooltip from 'antd/lib/tooltip';
import dashboardIcon from '../../../assets/dashboard-abstract.svg';
import Text from 'antd/lib/typography/Text';
import planeIllustrationUrl from '../../../assets/plane-illustration.svg';
import { trackOrgRouteEvent } from '../../actions/trackEvent';
import { setAppsDemoModeData, setWelcomeModalOptions } from '../../actions/ui';
import { createAppsDemoModeData } from '../../helpers/demoHelpers';
import { createAppRoute } from '../../helpers/routeHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../reducers/reducers';
import { selectOrg, selectUserIsRunwayAdmin } from '../../selectors/selectors';
import * as AnalyticEvents from '../../utils/analytics';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { SubscriptionFeatureType } from '../../types';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { isDevelopmentEnvironment } from '../../helpers';
import TrackedButton from '@/helpers/TrackedButton';

interface OnboardingCTAsProps {
    onAddAppClicked: () => void;
}

export const OnboardingCTAs: React.FC<OnboardingCTAsProps> = (props: OnboardingCTAsProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const orgId = useSelector((state: RootState) => selectOrg(state)!.id);
    const orgIdParam = useSelector((state: RootState) => selectOrg(state)!.orgId);
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));

    const isDevEnvironment = isDevelopmentEnvironment();
    const enableAddAppsOverride = isDevEnvironment || isRunwayAdmin;

    const viewInDemoMode = () => {
        const demoData = createAppsDemoModeData();

        dispatch(setAppsDemoModeData(demoData));

        // navigate to first app in demo app group
        const appRoute = createAppRoute(orgIdParam, demoData.apps[0].app.appId);

        history.replace(appRoute);

        dispatch(trackOrgRouteEvent({ eventName: AnalyticEvents.Event.ViewAppsDemoMode, orgId }));
        dispatch(
            setWelcomeModalOptions({
                shouldShowWelcomeModal: false,
            }),
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'scroll',
                marginBottom: 40,
                marginLeft: 40,
            }}
        >
            <div
                style={{
                    flexGrow: 1,
                    display: 'flex',
                    marginTop: 24,
                    marginBottom: 24,
                    alignItems: 'center',
                }}
            >
                <img src={planeIllustrationUrl} width={50} height={50} />
                <Title style={{ marginLeft: 16, marginTop: 8 }} level={3}>
                    Welcome to Runway.
                </Title>
            </div>
            <Space direction="vertical" size={36}>
                <CTACard
                    title="Mobile release management"
                    description="A better way to ship mobile apps. Add an app to get started."
                    helperText="Improve collaboration and transparency, and put your app releases on autopilot by giving your entire mobile org a home."
                    iconSrc={dashboardIcon}
                    color={`${runwayColors.primary}05`}
                    borderColor={`${runwayColors.primary}30`}
                    buttons={[
                        <PlanRestrictedFeatureWrapper
                            feature={SubscriptionFeatureType.AddApps}
                            requiresTooltip
                            key="add"
                        >
                            {(isDisabled) => (
                                <PermissionedActionButton
                                    actionType={RunwayUserAction.UACreateDeleteApp}
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={props.onAddAppClicked}
                                    disabled={isDisabled && !enableAddAppsOverride}
                                >
                                    Add app
                                </PermissionedActionButton>
                            )}
                        </PlanRestrictedFeatureWrapper>,
                        <TrackedButton
                            key="demo"
                            icon={<ExperimentOutlined color={runwayColors.primary} />}
                            onClick={viewInDemoMode}
                        >
                            Try it in demo mode
                        </TrackedButton>,
                        <Tooltip
                            key="tooltip"
                            style={{ width: 21, height: 21 }}
                            title={
                                <Text style={{ color: runwayColors.white }}>
                                    {
                                        '✨ Try Runway in demo mode to see how the dashboard will look with all integrations connected.'
                                    }
                                    <br />
                                    <Text style={{ color: antGrey.grey6, fontWeight: 400, fontSize: 12 }}>
                                        Demo mode is read-only — all actions on the dashboard will be disabled.
                                    </Text>
                                </Text>
                            }
                        >
                            <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                        </Tooltip>,
                    ]}
                ></CTACard>
            </Space>
        </div>
    );
};

const CTACard: React.FC<{
    title: string;
    description: string;
    buttons: React.ReactNode[];
    helperText: string;
    iconSrc: string;
    color?: string;
    borderColor?: string;
}> = (props) => {
    return (
        <Space size={42} style={{ alignItems: 'stretch' }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 16,
                    backgroundColor: props.color ?? antGrey.grey2,
                    borderColor: props.borderColor ?? antGrey.grey5,
                    borderRadius: 8,
                    flexGrow: 1,
                    borderWidth: 3,
                    borderStyle: 'solid',
                    width: 680,
                }}
            >
                <Space size={36} style={{ alignItems: 'stretch' }}>
                    <img style={{ width: 200 }} src={props.iconSrc}></img>
                    <div style={{ height: '100%', display: 'grid' }}>
                        <Title style={{ color: runwayColors.darkTextGrey }} level={5}>
                            {props.title}
                        </Title>
                        <Text style={{ maxWidth: 500, display: 'block', color: runwayColors.darkTextGrey }}>
                            {props.description}
                        </Text>
                        <div style={{ alignItems: 'end', display: 'flex', marginBottom: 20 }}>
                            <Space size={16}>{props.buttons}</Space>
                        </div>
                    </div>
                </Space>
            </div>
            <Divider type="vertical" style={{ height: '100%' }}></Divider>
            <div style={{ width: 220 }}>
                <InfoTip>{props.helperText}</InfoTip>
            </div>
        </Space>
    );
};
